import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { DistributedReport } from '../../../models/api/reports/distributed-report';
import { DistributedReportCriteria } from '../../../models/api/reports/distributed-report-criteria';
import { BytesPipe } from '../../../pipes/bytes.pipe';
import { ReportsService } from '../../../services/reports/reports.service';
import { BaseComponent } from '../../base/base.component';
import { HelpIconComponent } from '../../core/labels/help-icon.component';
import { IncludeLabelComponent } from '../../core/labels/include-label.component';
import { DetailItemComponent } from '../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../core/panels/detail-panel/detail-panel.component';

@Component({
  selector: 'app-distributed-request-view',
  imports: [
    DetailPanelComponent,
    DetailItemComponent,
    CommonModule,
    HelpIconComponent,
    IncludeLabelComponent,
    BytesPipe,
  ],
  template: `
    <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-8 tw-w-full">
      <div class="sm:tw-basis-[40%]">
        <app-detail-panel [dataSource]="dataSource">
          <app-detail-item label="Name" name="reportName" />
          <app-detail-item label="Base report" name="reportBase">
            <ng-template #value>
              <div class="tw-flex tw-items-center tw-gap-2">
                <span>{{ dataSource.reportBase }}</span>
                @if (reportDescription) {
                  <app-help-icon
                    [helpText]="reportDescription"
                    [ariaLabel]="
                      (dataSource.reportBase ?? 'Base report') + ' information'
                    "
                  />
                }
              </div>
            </ng-template>
          </app-detail-item>
          <app-detail-item
            label="Completed"
            name="reportCompleted"
            format="dt"
          />
          <app-detail-item
            label="Requested"
            name="reportCompleted"
            format="dt"
          />
          @if (asOfDate) {
            <app-detail-item label="As of Date">
              <ng-template #value>{{ asOfDate }}</ng-template>
            </app-detail-item>
          }
        </app-detail-panel>
      </div>

      <div class="sm:tw-basis-[30%]">
        <app-detail-panel [dataSource]="dataSource">
          <app-detail-item
            [label]="dataSource.fileFormat === 'PDF' ? 'Pages' : 'Rows'"
            name="fileNumPages"
          />
          <app-detail-item label="Size">
            <ng-template #value>
              {{ +dataSource.fileSize | bytes }}
            </ng-template>
          </app-detail-item>
          <app-detail-item label="Format" name="fileFormat" />
          @if (dataSource.fileFormat !== 'PDF') {
            <app-detail-item label="Auto-fit column widths">
              <ng-template #value>
                {{ criteria?.autoFitColumnWidths || '-' }}
              </ng-template>
            </app-detail-item>
          }
        </app-detail-panel>
      </div>

      <div class="sm:tw-basis-[30%]">
        <app-detail-panel [dataSource]="dataSource">
          <app-detail-item label="Include">
            <ng-template #value>
              <app-include-label
                [include]="criteria?.includeItems || []"
                [exclude]="criteria?.excludeItems || []"
              />
            </ng-template>
          </app-detail-item>
        </app-detail-panel>
      </div>
    </div>
  `,
  styles: ``,
})
export class DistributedRequestViewComponent
  extends BaseComponent
  implements OnInit
{
  private reportsService = inject(ReportsService);

  @Input()
  dataSource!: DistributedReport;

  @Input()
  criteria: DistributedReportCriteria | null = null;

  @Input()
  externalAccess = false;

  reportDescription: string;

  get asOfDate(): string | undefined {
    return this.criteria?.otherCriteria?.find(
      (item) => item.label === 'As of Date',
    )?.value;
  }

  async ngOnInit() {
    this.reportDescription = this.externalAccess
      ? await this.reportsService.getExternalReportDescriptionByName(
          this.dataSource.reportBase,
        )
      : await this.reportsService.getReportDescriptionByName(
          this.dataSource.reportBase,
        );
  }
}
